import { ConeGeometry, MeshBasicMaterial, Mesh, LineLoop, Group, Vector3, Geometry, OctahedronBufferGeometry, DoubleSide, PlaneGeometry, Quaternion, Math as Math$1, Vector2, Plane as Plane$1, Raycaster as Raycaster$1, Object3D } from 'three';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var DEFAULT_LINE_HEIGHT = 1;
var DEFAULT_RADIAL_SEGMENTS = 32;
var DEFAULT_CONE_HEIGHT = 0.75;
var DEFAULT_CONE_RADIUS = 0.3;
var DEFAULT_OCTAHEDRON_RADIUS = 0.1;
var DEFAULT_PLANE_WIDTH = 0.75;
var DEFAULT_PLANE_HEIGHT = 0.75;
var DEFAULT_PLANE_SEGMENTS = 32;
var DEFAULT_RING_NUM_POINTS = 64;
var DEFAULT_RING_RADIUS = 1;
var PICK_PLANE_OPACITY;
(function (PICK_PLANE_OPACITY) {
    PICK_PLANE_OPACITY[PICK_PLANE_OPACITY["ACTIVE"] = 0.75] = "ACTIVE";
    PICK_PLANE_OPACITY[PICK_PLANE_OPACITY["INACTIVE"] = 0.3] = "INACTIVE";
})(PICK_PLANE_OPACITY || (PICK_PLANE_OPACITY = {}));
var DEFAULT_TRANSLATION_CONTROLS_SEPARATION = 0.5;
var DEFAULT_COLOR_ARROW = "#f0ff00";
var DEFAULT_COLOR_RING = "#f0ff00";
var DEFAULT_COLOR_PLANE = "#f0ff00";
var DEFAULT_CONTROLS_OPACITY = 1;

var Cone = /** @class */ (function (_super) {
    __extends(Cone, _super);
    function Cone(color) {
        var _this = _super.call(this) || this;
        _this.geometry = new ConeGeometry(DEFAULT_CONE_RADIUS, DEFAULT_CONE_HEIGHT, DEFAULT_RADIAL_SEGMENTS);
        _this.material = new MeshBasicMaterial({ color: color, depthTest: false });
        _this.material.transparent = true;
        _this.material.opacity = DEFAULT_CONTROLS_OPACITY;
        return _this;
    }
    return Cone;
}(Mesh));

var Line = /** @class */ (function (_super) {
    __extends(Line, _super);
    function Line(color, geometry) {
        var _this = _super.call(this) || this;
        _this.geometry = geometry;
        _this.material = new MeshBasicMaterial({ color: color, depthTest: true });
        _this.material.transparent = true;
        _this.material.opacity = DEFAULT_CONTROLS_OPACITY;
        return _this;
    }
    return Line;
}(LineLoop));

var HandleGroup = /** @class */ (function (_super) {
    __extends(HandleGroup, _super);
    function HandleGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return HandleGroup;
}(Group));
var TranslationGroup = /** @class */ (function (_super) {
    __extends(TranslationGroup, _super);
    function TranslationGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return TranslationGroup;
}(HandleGroup));
var RotationGroup = /** @class */ (function (_super) {
    __extends(RotationGroup, _super);
    function RotationGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return RotationGroup;
}(HandleGroup));
var PickGroup = /** @class */ (function (_super) {
    __extends(PickGroup, _super);
    function PickGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return PickGroup;
}(HandleGroup));
var PickPlaneGroup = /** @class */ (function (_super) {
    __extends(PickPlaneGroup, _super);
    function PickPlaneGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return PickPlaneGroup;
}(HandleGroup));

var Translation = /** @class */ (function (_super) {
    __extends(Translation, _super);
    function Translation(color) {
        if (color === void 0) { color = DEFAULT_COLOR_ARROW; }
        var _this = _super.call(this) || this;
        _this.parallel = new Vector3(0, 1, 0);
        _this.getInteractiveObjects = function () {
            return [_this.cone];
        };
        _this.setColor = function (color) {
            var coneMaterial = _this.cone.material;
            var lineMaterial = _this.line.material;
            coneMaterial.color.set(color);
            lineMaterial.color.set(color);
        };
        _this.cone = new Cone(color);
        var lineGeometry = new Geometry();
        lineGeometry.vertices.push(new Vector3(0, 0, 0));
        lineGeometry.vertices.push(new Vector3(0, DEFAULT_LINE_HEIGHT, 0));
        _this.line = new Line(color, lineGeometry);
        _this.cone.geometry.scale(DEFAULT_CONE_RADIUS, DEFAULT_CONE_HEIGHT, DEFAULT_CONE_RADIUS);
        _this.cone.translateY(DEFAULT_LINE_HEIGHT);
        _this.add(_this.cone);
        _this.add(_this.line);
        return _this;
    }
    return Translation;
}(TranslationGroup));

var Octahedron = /** @class */ (function (_super) {
    __extends(Octahedron, _super);
    function Octahedron(color) {
        var _this = _super.call(this) || this;
        _this.geometry = new OctahedronBufferGeometry(DEFAULT_OCTAHEDRON_RADIUS, 0);
        _this.material = new MeshBasicMaterial({
            color: color,
            depthTest: false,
            transparent: true,
            side: DoubleSide
        });
        return _this;
    }
    return Octahedron;
}(Mesh));

var Rotation = /** @class */ (function (_super) {
    __extends(Rotation, _super);
    function Rotation(color, ringRadius) {
        if (color === void 0) { color = DEFAULT_COLOR_RING; }
        if (ringRadius === void 0) { ringRadius = DEFAULT_RING_RADIUS; }
        var _this = _super.call(this) || this;
        _this.getInteractiveObjects = function () {
            return [_this.handlebar];
        };
        _this.setColor = function (color) {
            var ringMaterial = _this.ring.material;
            var handlebarMaterial = _this.handlebar.material;
            ringMaterial.color.set(color);
            handlebarMaterial.color.set(color);
        };
        var ringNumberOfPoints = DEFAULT_RING_NUM_POINTS;
        var ringGeometry = new Geometry();
        var angle = (2 * Math.PI) / ringNumberOfPoints;
        for (var i = 1; i < ringNumberOfPoints + 1; i++) {
            ringGeometry.vertices.push(new Vector3(ringRadius * Math.cos(i * angle), ringRadius * Math.sin(i * angle), 0));
        }
        _this.ring = new Line(color, ringGeometry);
        _this.handlebar = new Octahedron(color);
        _this.handlebar.position.y = ringRadius;
        _this.add(_this.ring);
        _this.add(_this.handlebar);
        return _this;
    }
    return Rotation;
}(RotationGroup));

var Pick = /** @class */ (function (_super) {
    __extends(Pick, _super);
    function Pick() {
        var _this = _super.call(this) || this;
        _this.getInteractiveObjects = function () {
            return [_this.octahedron];
        };
        _this.setColor = function (color) {
            var octahedronMaterial = _this.octahedron.material;
            octahedronMaterial.color.set(color);
        };
        _this.octahedron = new Octahedron("white");
        _this.add(_this.octahedron);
        return _this;
    }
    return Pick;
}(PickGroup));

var Plane = /** @class */ (function (_super) {
    __extends(Plane, _super);
    function Plane(color) {
        if (color === void 0) { color = DEFAULT_COLOR_RING; }
        var _this = _super.call(this) || this;
        _this.geometry = new PlaneGeometry(DEFAULT_PLANE_WIDTH, DEFAULT_PLANE_HEIGHT, DEFAULT_PLANE_SEGMENTS);
        _this.material = new MeshBasicMaterial({
            color: color,
            depthTest: false,
            side: DoubleSide,
            transparent: true
        });
        _this.material.opacity = PICK_PLANE_OPACITY.INACTIVE;
        return _this;
    }
    return Plane;
}(Mesh));

var PickPlane = /** @class */ (function (_super) {
    __extends(PickPlane, _super);
    function PickPlane(color) {
        if (color === void 0) { color = DEFAULT_COLOR_PLANE; }
        var _this = _super.call(this) || this;
        _this.getInteractiveObjects = function () {
            return [_this.plane];
        };
        _this.setColor = function (color) {
            var planeMaterial = _this.plane.material;
            var boundaryMaterial = _this.boundary.material;
            planeMaterial.color.set(color);
            boundaryMaterial.color.set(color);
        };
        var boundaryGeometry = new Geometry();
        var crossXGeometry = new Geometry();
        var crossYGeometry = new Geometry();
        var vertexMaxX = DEFAULT_PLANE_WIDTH / 2;
        var vertexMaxY = DEFAULT_PLANE_HEIGHT / 2;
        boundaryGeometry.vertices.push(new Vector3(vertexMaxX, vertexMaxY, 0));
        boundaryGeometry.vertices.push(new Vector3(vertexMaxX, -vertexMaxY, 0));
        boundaryGeometry.vertices.push(new Vector3(-vertexMaxX, -vertexMaxY, 0));
        boundaryGeometry.vertices.push(new Vector3(-vertexMaxX, vertexMaxY, 0));
        crossXGeometry.vertices.push(new Vector3(0, vertexMaxY, 0));
        crossXGeometry.vertices.push(new Vector3(0, -vertexMaxY, 0));
        crossYGeometry.vertices.push(new Vector3(-vertexMaxX, 0, 0));
        crossYGeometry.vertices.push(new Vector3(vertexMaxX, 0, 0));
        _this.boundary = new Line(color, boundaryGeometry);
        _this.crossX = new Line("black", crossXGeometry);
        _this.crossY = new Line("black", crossYGeometry);
        _this.plane = new Plane(color);
        _this.add(_this.plane);
        _this.add(_this.boundary);
        _this.add(_this.crossX);
        _this.add(_this.crossY);
        return _this;
    }
    return PickPlane;
}(PickPlaneGroup));

var RotationEye = /** @class */ (function (_super) {
    __extends(RotationEye, _super);
    function RotationEye(color) {
        if (color === void 0) { color = DEFAULT_COLOR_RING; }
        var _this = _super.call(this, color, 1.25) || this;
        _this.camera = null;
        _this.controlsWorldOrientation = new Quaternion();
        _this._temp1 = new Vector3();
        _this._temp2 = new Vector3();
        _this._temp3 = new Quaternion();
        _this.worldPosition = new Vector3();
        return _this;
    }
    RotationEye.prototype.updateMatrixWorld = function (force) {
        if (this.camera !== null && this.parent !== null) {
            this.parent.matrixWorld.decompose(this._temp1, this.controlsWorldOrientation, this._temp2);
            this.matrixWorld.decompose(this.worldPosition, this._temp3, this._temp2);
            this.camera
                .getWorldQuaternion(this.quaternion)
                .premultiply(this.controlsWorldOrientation.inverse());
            this.camera.getWorldPosition(this.up).sub(this.worldPosition);
        }
        _super.prototype.updateMatrixWorld.call(this, force);
    };
    return RotationEye;
}(Rotation));

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as the `TypeError` message for "Functions" methods. */
var FUNC_ERROR_TEXT = 'Expected a function';

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** `Object#toString` result references. */
var funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    symbolTag = '[object Symbol]';

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/,
    reLeadingDot = /^\./,
    rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Checks if `value` is a host object in IE < 9.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a host object, else `false`.
 */
function isHostObject(value) {
  // Many host objects are `Object` objects that can coerce to strings
  // despite having improperly defined `toString` methods.
  var result = false;
  if (value != null && typeof value.toString != 'function') {
    try {
      result = !!(value + '');
    } catch (e) {}
  }
  return result;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype,
    funcProto = Function.prototype,
    objectProto = Object.prototype;

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/** Built-in value references. */
var Symbol = root.Symbol,
    splice = arrayProto.splice;

/* Built-in method references that are verified to be native. */
var Map = getNative(root, 'Map'),
    nativeCreate = getNative(Object, 'create');

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries ? entries.length : 0;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  return this.has(key) && delete this.__data__[key];
}

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty.call(data, key) ? data[key] : undefined;
}

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? data[key] !== undefined : hasOwnProperty.call(data, key);
}

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
  return this;
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries ? entries.length : 0;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
}

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries ? entries.length : 0;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map || ListCache),
    'string': new Hash
  };
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  return getMapData(this, key)['delete'](key);
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  getMapData(this, key).set(key, value);
  return this;
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = isKey(path, object) ? [path] : castPath(path);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = (isFunction(value) || isHostObject(value)) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value) {
  return isArray(value) ? value : stringToPath(value);
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = memoize(function(string) {
  string = toString(string);

  var result = [];
  if (reLeadingDot.test(string)) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, string) {
    result.push(quote ? string.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to process.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result);
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache);
  return memoized;
}

// Assign cache to `_.memoize`.
memoize.Cache = MapCache;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 8-9 which returns 'object' for typed array and other constructors.
  var tag = isObject(value) ? objectToString.call(value) : '';
  return tag == funcTag || tag == genTag;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && objectToString.call(value) == symbolTag);
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

var lodash_get = get;

var DEFAULT_HANDLE_GROUP_NAME;
(function (DEFAULT_HANDLE_GROUP_NAME) {
    DEFAULT_HANDLE_GROUP_NAME["XPT"] = "xpt_handle";
    DEFAULT_HANDLE_GROUP_NAME["YPT"] = "ypt_handle";
    DEFAULT_HANDLE_GROUP_NAME["ZPT"] = "zpt_handle";
    DEFAULT_HANDLE_GROUP_NAME["XNT"] = "xnt_handle";
    DEFAULT_HANDLE_GROUP_NAME["YNT"] = "ynt_handle";
    DEFAULT_HANDLE_GROUP_NAME["ZNT"] = "znt_handle";
    DEFAULT_HANDLE_GROUP_NAME["XR"] = "xr_handle";
    DEFAULT_HANDLE_GROUP_NAME["YR"] = "yr_handle";
    DEFAULT_HANDLE_GROUP_NAME["ZR"] = "zr_handle";
    DEFAULT_HANDLE_GROUP_NAME["ER"] = "er_handle";
    DEFAULT_HANDLE_GROUP_NAME["PICK"] = "pick_handle";
    DEFAULT_HANDLE_GROUP_NAME["PICK_PLANE_XY"] = "pick_plane_xy_handle";
    DEFAULT_HANDLE_GROUP_NAME["PICK_PLANE_YZ"] = "pick_plane_yz_handle";
    DEFAULT_HANDLE_GROUP_NAME["PICK_PLANE_ZX"] = "pick_plane_zx_handle";
})(DEFAULT_HANDLE_GROUP_NAME || (DEFAULT_HANDLE_GROUP_NAME = {}));
var ANCHOR_MODE;
(function (ANCHOR_MODE) {
    ANCHOR_MODE["FIXED"] = "fixed";
    ANCHOR_MODE["INHERIT"] = "inherit";
})(ANCHOR_MODE || (ANCHOR_MODE = {}));
var Controls = /** @class */ (function (_super) {
    __extends(Controls, _super);
    function Controls(object, camera, options) {
        var _this = _super.call(this) || this;
        _this.object = object;
        _this.camera = camera;
        _this.handleTargetQuaternion = new Quaternion();
        _this.objectWorldPosition = new Vector3();
        _this.objectTargetPosition = new Vector3();
        _this.objectTargetQuaternion = new Quaternion();
        _this.objectParentWorldPosition = new Vector3();
        _this.objectParentWorldQuaternion = new Quaternion();
        _this.objectParentWorldScale = new Vector3();
        _this.deltaPosition = new Vector3();
        _this.normalizedHandleParallelVectorCache = new Vector3();
        _this.touch1 = new Vector3();
        _this.touch2 = new Vector3();
        _this.minBox = new Vector3();
        _this.maxBox = new Vector3();
        _this.dragStartPoint = new Vector3();
        _this.dragIncrementalStartPoint = new Vector3();
        _this.handleNamesMap = {};
        _this.isBeingDraggedTranslation = false;
        _this.isBeingDraggedRotation = false;
        _this.isDampingEnabled = true;
        _this.dampingFactor = 0.8;
        _this.initialSelfQuaternion = new Quaternion();
        _this.setupDefaultPickPlane = function () {
            _this.pickPlaneXY.name = DEFAULT_HANDLE_GROUP_NAME.PICK_PLANE_XY;
            _this.pickPlaneYZ.name = DEFAULT_HANDLE_GROUP_NAME.PICK_PLANE_YZ;
            _this.pickPlaneZX.name = DEFAULT_HANDLE_GROUP_NAME.PICK_PLANE_ZX;
            _this.pickPlaneYZ.up = new Vector3(1, 0, 0);
            _this.pickPlaneZX.up = new Vector3(0, 1, 0);
            _this.pickPlaneXY.up = new Vector3(0, 0, 1);
            _this.pickPlaneYZ.rotateY(Math.PI / 2);
            _this.pickPlaneZX.rotateX(Math.PI / 2);
            _this.handleNamesMap[_this.pickPlaneXY.name] = _this.pickPlaneXY;
            _this.handleNamesMap[_this.pickPlaneYZ.name] = _this.pickPlaneYZ;
            _this.handleNamesMap[_this.pickPlaneZX.name] = _this.pickPlaneZX;
            _this.add(_this.pickPlaneXY);
            _this.add(_this.pickPlaneYZ);
            _this.add(_this.pickPlaneZX);
        };
        _this.setupHandle = function (handle) {
            var existingHandle = _this.handleNamesMap[handle.name];
            if (existingHandle !== undefined) {
                throw new Error("handle with this name already exists!");
            }
            _this.handleNamesMap[handle.name] = handle;
            _this.add(handle);
        };
        _this.setupDefaultPick = function () {
            _this.pick.name = DEFAULT_HANDLE_GROUP_NAME.PICK;
            _this.handleNamesMap[_this.pick.name] = _this.pick;
            _this.add(_this.pick);
        };
        _this.setupDefaultEyeRotation = function () {
            _this.rotationEye.name = DEFAULT_HANDLE_GROUP_NAME.ER;
            _this.handleNamesMap[_this.rotationEye.name] = _this.rotationEye;
            _this.rotationEye.camera = _this.camera;
            _this.add(_this.rotationEye);
        };
        _this.setupDefaultTranslation = function () {
            _this.translationXP.name = DEFAULT_HANDLE_GROUP_NAME.XPT;
            _this.translationYP.name = DEFAULT_HANDLE_GROUP_NAME.YPT;
            _this.translationZP.name = DEFAULT_HANDLE_GROUP_NAME.ZPT;
            _this.translationXN.name = DEFAULT_HANDLE_GROUP_NAME.XNT;
            _this.translationYN.name = DEFAULT_HANDLE_GROUP_NAME.YNT;
            _this.translationZN.name = DEFAULT_HANDLE_GROUP_NAME.ZNT;
            _this.translationXP.translateX(_this.maxBox.x);
            _this.translationYP.translateY(_this.maxBox.y);
            _this.translationZP.translateZ(_this.maxBox.z);
            _this.translationXN.translateX(_this.minBox.x);
            _this.translationYN.translateY(_this.minBox.y);
            _this.translationZN.translateZ(_this.minBox.z);
            _this.translationXP.rotateZ(-Math.PI / 2);
            _this.translationZP.rotateX(Math.PI / 2);
            _this.translationXN.rotateZ(Math.PI / 2);
            _this.translationYN.rotateX(Math.PI);
            _this.translationZN.rotateX(-Math.PI / 2);
            _this.translationXP.up = new Vector3(0, 1, 0);
            _this.translationYP.up = new Vector3(0, 0, 1);
            _this.translationZP.up = new Vector3(0, 1, 0);
            _this.translationXN.up = new Vector3(0, -1, 0);
            _this.translationYN.up = new Vector3(0, 0, -1);
            _this.translationZN.up = new Vector3(0, -1, 0);
            _this.translationXP.parallel = new Vector3(1, 0, 0);
            _this.translationYP.parallel = new Vector3(0, 1, 0);
            _this.translationZP.parallel = new Vector3(0, 0, 1);
            _this.translationXN.parallel = new Vector3(-1, 0, 0);
            _this.translationYN.parallel = new Vector3(0, -1, 0);
            _this.translationZN.parallel = new Vector3(0, 0, -1);
            _this.handleNamesMap[_this.translationXP.name] = _this.translationXP;
            _this.handleNamesMap[_this.translationYP.name] = _this.translationYP;
            _this.handleNamesMap[_this.translationZP.name] = _this.translationZP;
            _this.handleNamesMap[_this.translationXN.name] = _this.translationXN;
            _this.handleNamesMap[_this.translationYN.name] = _this.translationYN;
            _this.handleNamesMap[_this.translationZN.name] = _this.translationZN;
            _this.add(_this.translationXP);
            _this.add(_this.translationYP);
            _this.add(_this.translationZP);
            _this.add(_this.translationXN);
            _this.add(_this.translationYN);
            _this.add(_this.translationZN);
        };
        _this.setupDefaultRotation = function () {
            _this.rotationX.name = DEFAULT_HANDLE_GROUP_NAME.XR;
            _this.rotationY.name = DEFAULT_HANDLE_GROUP_NAME.YR;
            _this.rotationZ.name = DEFAULT_HANDLE_GROUP_NAME.ZR;
            _this.rotationX.up = new Vector3(1, 0, 0);
            _this.rotationY.up = new Vector3(0, 1, 0);
            _this.rotationZ.up = new Vector3(0, 0, 1);
            _this.rotationX.rotateY(Math.PI / 2);
            _this.rotationX.rotateZ(Math.PI);
            _this.rotationY.rotateX(Math.PI / 2);
            _this.handleNamesMap[_this.rotationX.name] = _this.rotationX;
            _this.handleNamesMap[_this.rotationY.name] = _this.rotationY;
            _this.handleNamesMap[_this.rotationZ.name] = _this.rotationZ;
            _this.add(_this.rotationX);
            _this.add(_this.rotationY);
            _this.add(_this.rotationZ);
        };
        _this.computeObjectBounds = function () {
            if (_this.options.separationT !== undefined) {
                var _a = _this.options.separationT, x = _a.x, y = _a.y, z = _a.z;
                _this.minBox.copy(new Vector3(-x, -y, -z));
                _this.maxBox.copy(new Vector3(x, y, z));
            }
            else if (_this.object.type === "Mesh") {
                var geometry = _this.object.geometry;
                geometry.computeBoundingBox();
                var _b = geometry.boundingBox, min = _b.min, max = _b.max;
                _this.minBox.copy(min);
                _this.maxBox.copy(max);
                _this.minBox.addScalar(-DEFAULT_TRANSLATION_CONTROLS_SEPARATION);
                _this.maxBox.addScalar(DEFAULT_TRANSLATION_CONTROLS_SEPARATION);
            }
            else {
                _this.minBox.copy(new Vector3(-1, -1, -1));
                _this.maxBox.copy(new Vector3(1, 1, 1));
            }
        };
        _this.processDragStart = function (args) {
            var point = args.point, handle = args.handle;
            _this.dragStartPoint.copy(point);
            _this.dragIncrementalStartPoint.copy(point);
            _this.isBeingDraggedTranslation =
                handle instanceof TranslationGroup ||
                    handle instanceof PickGroup ||
                    handle instanceof PickPlaneGroup;
            _this.isBeingDraggedRotation = handle instanceof RotationGroup;
        };
        _this.setDampingFactor = function (dampingFactor) {
            if (dampingFactor === void 0) { dampingFactor = 0; }
            return (_this.dampingFactor = Math$1.clamp(dampingFactor, 0, 1));
        };
        _this.processHandle = function (args) {
            var point = args.point, handle = args.handle, _a = args.dragRatio, dragRatio = _a === void 0 ? 1 : _a;
            var k = Math.exp(-_this.dampingFactor * Math.abs(Math.pow(dragRatio, 3)));
            if (handle instanceof TranslationGroup) {
                _this.deltaPosition.copy(point).sub(_this.dragIncrementalStartPoint);
                _this.normalizedHandleParallelVectorCache
                    .copy(handle.parallel.normalize())
                    .applyQuaternion(_this.quaternion);
                var delta = _this.deltaPosition.dot(_this.normalizedHandleParallelVectorCache);
                _this.deltaPosition
                    .copy(_this.normalizedHandleParallelVectorCache)
                    .multiplyScalar(_this.isDampingEnabled ? k * delta : delta);
                _this.position.add(_this.deltaPosition);
            }
            else if (handle instanceof PickGroup || handle instanceof PickPlaneGroup) {
                _this.deltaPosition
                    .copy(point)
                    .sub(_this.dragIncrementalStartPoint)
                    .multiplyScalar(_this.isDampingEnabled ? k : 1);
                _this.position.add(_this.deltaPosition);
            }
            else {
                _this.touch1
                    .copy(_this.dragIncrementalStartPoint)
                    .sub(_this.objectWorldPosition)
                    .normalize();
                _this.touch2
                    .copy(point)
                    .sub(_this.objectWorldPosition)
                    .normalize();
                _this.handleTargetQuaternion.setFromUnitVectors(_this.touch1, _this.touch2);
                if (_this.attachMode === ANCHOR_MODE.FIXED) {
                    _this.detachHandleUpdateQuaternionAttach(handle, _this.handleTargetQuaternion);
                }
            }
            _this.objectTargetQuaternion.premultiply(_this.handleTargetQuaternion);
            _this.dragIncrementalStartPoint.copy(point);
        };
        _this.detachObjectUpdatePositionAttach = function (parent, object) {
            if (parent !== null && _this.parent !== null && _this.parent.parent !== null) {
                var scene = _this.parent.parent;
                if (scene.type !== "Scene") {
                    throw new Error("freeform controls must be attached to the scene");
                }
                scene.attach(object);
                object.position.copy(_this.objectTargetPosition);
                parent.attach(object);
            }
        };
        _this.detachHandleUpdateQuaternionAttach = function (handle, quaternion) {
            if (_this.parent !== null && _this.parent.parent) {
                var scene = _this.parent.parent;
                if (scene.type !== "Scene") {
                    throw new Error("freeform controls must be attached to the scene");
                }
                scene.attach(handle);
                handle.applyQuaternion(quaternion);
                _this.attach(handle);
            }
        };
        _this.showByNames = function (handleNames, visibility) {
            if (visibility === void 0) { visibility = true; }
            handleNames.map(function (handleName) {
                var handle = _this.handleNamesMap[handleName];
                if (handle === undefined) {
                    throw new Error("handle: " + handleName + " not found");
                }
                handle.visible = visibility;
            });
        };
        _this.showAll = function (visibility) {
            if (visibility === void 0) { visibility = true; }
            var handles = Object.values(_this.handleNamesMap);
            handles.map(function (handle) {
                handle.visible = visibility;
            });
        };
        _this.updateMatrixWorld = function (force) {
            _this.object.updateMatrixWorld(force);
            _this.object.getWorldPosition(_this.objectWorldPosition);
            var parent = _this.object.parent;
            if (parent !== null) {
                parent.matrixWorld.decompose(_this.objectParentWorldPosition, _this.objectParentWorldQuaternion, _this.objectParentWorldScale);
            }
            _this.objectParentWorldQuaternion.inverse();
            _this.objectTargetPosition.copy(_this.position);
            _this.objectTargetQuaternion.premultiply(_this.objectParentWorldQuaternion);
            if (_this.isBeingDraggedTranslation) {
                _this.detachObjectUpdatePositionAttach(parent, _this.object);
            }
            else if (_this.isBeingDraggedRotation) {
                _this.object.quaternion.copy(_this.objectTargetQuaternion);
                _this.detachObjectUpdatePositionAttach(parent, _this.object);
            }
            else {
                _this.position.copy(_this.objectWorldPosition);
            }
            _this.object.getWorldQuaternion(_this.objectTargetQuaternion);
            if (_this.attachMode === ANCHOR_MODE.INHERIT && !_this.isBeingDraggedTranslation) {
                _this.quaternion.copy(_this.initialSelfQuaternion).premultiply(_this.objectTargetQuaternion);
            }
            _super.prototype.updateMatrixWorld.call(_this, force);
        };
        _this.options = options || {};
        _this.attachMode = lodash_get(_this.options, "mode", ANCHOR_MODE.FIXED);
        _this.hideOtherHandlesOnSelect = lodash_get(_this.options, "hideOtherHandlesOnSelect", true);
        _this.hideOtherControlsInstancesOnSelect = lodash_get(_this.options, "hideOtherControlsInstancesOnSelect", true);
        _this.showHelperPlane = lodash_get(_this.options, "showHelperPlane", false);
        if (_this.options.orientation !== undefined) {
            var _a = _this.options.orientation, x = _a.x, y = _a.y, z = _a.z, w = _a.w;
            _this.initialSelfQuaternion.set(x, y, z, w).normalize();
            _this.quaternion.copy(_this.initialSelfQuaternion);
        }
        _this.computeObjectBounds();
        _this.pick = new Pick();
        _this.pickPlaneXY = new PickPlane("yellow");
        _this.pickPlaneYZ = new PickPlane("cyan");
        _this.pickPlaneZX = new PickPlane("pink");
        _this.translationXP = new Translation("red");
        _this.translationYP = new Translation("green");
        _this.translationZP = new Translation("blue");
        _this.translationXN = new Translation("red");
        _this.translationYN = new Translation("green");
        _this.translationZN = new Translation("blue");
        _this.rotationX = new Rotation("red");
        _this.rotationY = new Rotation("green");
        _this.rotationZ = new Rotation("blue");
        _this.rotationEye = new RotationEye("yellow");
        _this.setupDefaultTranslation();
        _this.setupDefaultRotation();
        _this.setupDefaultEyeRotation();
        _this.setupDefaultPickPlane();
        _this.setupDefaultPick();
        return _this;
    }
    Controls.prototype.getInteractiveObjects = function () {
        var handles = Object.values(this.handleNamesMap);
        var interactiveObjects = [];
        handles.map(function (handle) {
            if (!handle.visible) {
                return;
            }
            interactiveObjects.push.apply(interactiveObjects, handle.getInteractiveObjects());
        });
        return interactiveObjects;
    };
    return Controls;
}(Group));

var nanoevents = createCommonjsModule(function (module) {
(
  /**
   * Interface for event subscription.
   *
   * @example
   * var NanoEvents = require('nanoevents')
   *
   * class Ticker {
   *   constructor() {
   *     this.emitter = new NanoEvents()
   *   }
   *   on() {
   *     return this.emitter.on.apply(this.events, arguments)
   *   }
   *   tick() {
   *     this.emitter.emit('tick')
   *   }
   * }
   *
   * @alias NanoEvents
   * @class
   */
  module.exports = function NanoEvents () {
    /**
     * Event names in keys and arrays with listeners in values.
     * @type {object}
     *
     * @example
     * Object.keys(ee.events)
     *
     * @alias NanoEvents#events
     */
    this.events = { };
  }
).prototype = {

  /**
   * Calls each of the listeners registered for a given event.
   *
   * @param {string} event The event name.
   * @param {...*} arguments The arguments for listeners.
   *
   * @return {undefined}
   *
   * @example
   * ee.emit('tick', tickType, tickDuration)
   *
   * @alias NanoEvents#emit
   * @method
   */
  emit: function emit (event) {
    var args = [].slice.call(arguments, 1)
    // Array.prototype.call() returns empty array if context is not array-like
    ;[].slice.call(this.events[event] || []).filter(function (i) {
      i.apply(null, args);
    });
  },

  /**
   * Add a listener for a given event.
   *
   * @param {string} event The event name.
   * @param {function} cb The listener function.
   *
   * @return {function} Unbind listener from event.
   *
   * @example
   * const unbind = ee.on('tick', (tickType, tickDuration) => {
   *   count += 1
   * })
   *
   * disable () {
   *   unbind()
   * }
   *
   * @alias NanoEvents#on
   * @method
   */
  on: function on (event, cb) {

    (this.events[event] = this.events[event] || []).push(cb);

    return function () {
      this.events[event] = this.events[event].filter(function (i) {
        return i !== cb
      });
    }.bind(this)
  }
};
});

/**
 * Removes all listeners.
 *
 * @param {NanoEvents} emitter NanoEvents instance.
 *
 * @returns {undefined}
 *
 * @example
 * unbindAll(emitter)
 */
function unbindAll (emitter) {
  emitter.events = { };
}

var unbindAll_1 = unbindAll;

var emitter = new nanoevents();
var unbindAll$1 = function () { return unbindAll_1(emitter); };

var RAYCASTER_EVENTS;
(function (RAYCASTER_EVENTS) {
    RAYCASTER_EVENTS["DRAG_START"] = "DRAG_START";
    RAYCASTER_EVENTS["DRAG"] = "DRAG";
    RAYCASTER_EVENTS["DRAG_STOP"] = "DRAG_STOP";
})(RAYCASTER_EVENTS || (RAYCASTER_EVENTS = {}));
var Raycaster = /** @class */ (function (_super) {
    __extends(Raycaster, _super);
    function Raycaster(camera, domElement, controls) {
        var _this = _super.call(this) || this;
        _this.camera = camera;
        _this.domElement = domElement;
        _this.controls = controls;
        _this.mouse = new Vector2();
        _this.cameraPosition = new Vector3();
        _this.activeHandle = null;
        _this.activePlane = null;
        _this.point = new Vector3();
        _this.normal = new Vector3();
        _this.visibleHandles = [];
        _this.visibleControls = [];
        _this.helperPlane = new Plane("yellow");
        _this.controlsWorldQuaternion = new Quaternion();
        _this.activeHandleWorldQuaternion = new Quaternion();
        _this.clientDiagonalLength = 1;
        _this.previousScreenPoint = new Vector2();
        _this.currentScreenPoint = new Vector2();
        _this.mouseDownListener = function (event) {
            _this.setRayDirection(event);
            _this.clientDiagonalLength = Math.sqrt(Math.pow(event.target.clientWidth, 2) +
                Math.pow(event.target.clientHeight, 2));
            _this.previousScreenPoint.set(event.clientX, event.clientY);
            var interactiveObjects = [];
            Object.values(_this.controls).map(function (controls) {
                interactiveObjects.push.apply(interactiveObjects, controls.getInteractiveObjects());
            });
            _this.activeHandle = _this.resolveHandleGroup(_this.intersectObjects(interactiveObjects, true)[0]);
            if (_this.activeHandle !== null && _this.activeHandle.parent !== null) {
                _this.activePlane = new Plane$1();
                var controls = _this.activeHandle.parent;
                controls.getWorldQuaternion(_this.controlsWorldQuaternion);
                _this.normal.copy(_this.activeHandle instanceof PickGroup
                    ? _this.getEyePlaneNormal(_this.activeHandle)
                    : _this.activeHandle.up);
                if (!(_this.activeHandle instanceof RotationEye || _this.activeHandle instanceof PickGroup)) {
                    _this.normal.applyQuaternion(_this.controlsWorldQuaternion);
                }
                if (controls.hideOtherControlsInstancesOnSelect) {
                    Object.values(_this.controls).forEach(function (x) {
                        if (x.visible) {
                            _this.visibleControls.push(x);
                        }
                        x.visible = false;
                    });
                    controls.visible = true;
                }
                if (controls.hideOtherHandlesOnSelect) {
                    controls.children.map(function (handle) {
                        if (handle.visible) {
                            _this.visibleHandles.push(handle);
                        }
                        handle.visible = false;
                    });
                    _this.activeHandle.visible = true;
                }
                if (controls.showHelperPlane &&
                    !(_this.activeHandle instanceof Pick || _this.activeHandle instanceof PickPlane)) {
                    var scene = controls.parent;
                    _this.helperPlane.position.copy(controls.position);
                    _this.activeHandle.getWorldQuaternion(_this.helperPlane.quaternion);
                    scene.add(_this.helperPlane);
                }
                if (_this.activeHandle instanceof PickPlane) {
                    _this.setPickPlaneOpacity(PICK_PLANE_OPACITY.ACTIVE);
                }
                _this.activePlane.setFromNormalAndCoplanarPoint(_this.normal, controls.position);
                var initialIntersectionPoint = new Vector3();
                if (_this.activeHandle instanceof PickGroup) {
                    _this.activeHandle.getWorldPosition(initialIntersectionPoint);
                }
                else {
                    _this.ray.intersectPlane(_this.activePlane, initialIntersectionPoint);
                }
                _this.domElement.removeEventListener("mousedown", _this.mouseDownListener);
                emitter.emit(RAYCASTER_EVENTS.DRAG_START, {
                    point: initialIntersectionPoint,
                    handle: _this.activeHandle
                });
                _this.domElement.addEventListener("mousemove", _this.mouseMoveListener, false);
            }
            else {
                _this.activePlane = null;
            }
        };
        _this.getEyePlaneNormal = function (object) {
            _this.cameraPosition.copy(_this.camera.position);
            return _this.cameraPosition.sub(object.position);
        };
        _this.setRayDirection = function (event) {
            var rect = _this.domElement.getBoundingClientRect();
            var _a = _this.domElement, clientHeight = _a.clientHeight, clientWidth = _a.clientWidth;
            _this.mouse.x = ((event.clientX - rect.left) / clientWidth) * 2 - 1;
            _this.mouse.y = -((event.clientY - rect.top) / clientHeight) * 2 + 1;
            _this.setFromCamera(_this.mouse, _this.camera);
        };
        _this.mouseMoveListener = function (event) {
            if (_this.activeHandle === null || _this.activePlane === null) {
                return;
            }
            _this.activeHandle.getWorldQuaternion(_this.activeHandleWorldQuaternion);
            _this.helperPlane.quaternion.copy(_this.activeHandleWorldQuaternion);
            _this.setRayDirection(event);
            _this.ray.intersectPlane(_this.activePlane, _this.point);
            _this.currentScreenPoint.set(event.clientX, event.clientY);
            var distance = _this.currentScreenPoint.distanceTo(_this.previousScreenPoint);
            var dragRatio = distance / (_this.clientDiagonalLength || 1);
            emitter.emit(RAYCASTER_EVENTS.DRAG, {
                point: _this.point,
                handle: _this.activeHandle,
                dragRatio: dragRatio
            });
            _this.previousScreenPoint.set(event.clientX, event.clientY);
        };
        _this.mouseUpListener = function () {
            _this.domElement.removeEventListener("mousemove", _this.mouseMoveListener);
            _this.domElement.addEventListener("mousedown", _this.mouseDownListener, false);
            emitter.emit(RAYCASTER_EVENTS.DRAG_STOP, { point: _this.point, handle: _this.activeHandle });
            if (_this.activeHandle !== null &&
                _this.activeHandle.parent !== null &&
                _this.activeHandle.parent.hideOtherControlsInstancesOnSelect) {
                _this.visibleControls.forEach(function (controls) {
                    controls.visible = true;
                });
                _this.visibleControls = [];
            }
            if (_this.activeHandle !== null &&
                _this.activeHandle.parent !== null &&
                _this.activeHandle.parent.hideOtherHandlesOnSelect) {
                _this.visibleHandles.forEach(function (handle) {
                    handle.visible = true;
                });
                _this.visibleHandles = [];
            }
            if (_this.activeHandle instanceof PickPlane) {
                _this.setPickPlaneOpacity(PICK_PLANE_OPACITY.INACTIVE);
            }
            if (_this.activeHandle !== null &&
                _this.activeHandle.parent !== null &&
                _this.activeHandle.parent.showHelperPlane) {
                var scene = _this.activeHandle.parent.parent;
                scene.remove(_this.helperPlane);
            }
            _this.activeHandle = null;
            _this.activePlane = null;
        };
        _this.resolveHandleGroup = function (intersectedObject) {
            if (intersectedObject === undefined) {
                return null;
            }
            return intersectedObject.object.parent;
        };
        _this.destroy = function () {
            _this.activePlane = null;
            _this.activeHandle = null;
            _this.domElement.removeEventListener("mousedown", _this.mouseDownListener);
            _this.domElement.removeEventListener("mousemove", _this.mouseMoveListener);
            _this.domElement.removeEventListener("mouseup", _this.mouseUpListener);
        };
        _this.domElement.addEventListener("mousedown", _this.mouseDownListener, false);
        _this.domElement.addEventListener("mouseup", _this.mouseUpListener, false);
        return _this;
    }
    Raycaster.prototype.setPickPlaneOpacity = function (opacity) {
        if (!(this.activeHandle instanceof PickPlane)) {
            return;
        }
        var material = this.activeHandle.plane.material;
        if (Array.isArray(material)) {
            material.map(function (m) {
                m.opacity = opacity;
                m.needsUpdate = true;
            });
        }
        else {
            material.opacity = opacity;
            material.needsUpdate = true;
        }
    };
    return Raycaster;
}(Raycaster$1));

var ControlsManager = /** @class */ (function (_super) {
    __extends(ControlsManager, _super);
    function ControlsManager(camera, domElement) {
        var _a;
        var _this = _super.call(this) || this;
        _this.camera = camera;
        _this.domElement = domElement;
        _this.objects = {};
        _this.controls = {};
        _this.eventListeners = (_a = {},
            _a[RAYCASTER_EVENTS.DRAG_START] = [],
            _a[RAYCASTER_EVENTS.DRAG] = [],
            _a[RAYCASTER_EVENTS.DRAG_STOP] = [],
            _a);
        _this.listenToEvents = function () {
            emitter.on(RAYCASTER_EVENTS.DRAG_START, function (_a) {
                var point = _a.point, handle = _a.handle;
                if (handle === null) {
                    return;
                }
                var controls = handle.parent;
                if (controls === null) {
                    return;
                }
                controls.processDragStart({ point: point, handle: handle });
                _this.eventListeners[RAYCASTER_EVENTS.DRAG_START].map(function (callback) {
                    callback(controls.object, handle.name);
                });
            });
            emitter.on(RAYCASTER_EVENTS.DRAG, function (_a) {
                var point = _a.point, handle = _a.handle, dragRatio = _a.dragRatio;
                if (handle === null) {
                    return;
                }
                var controls = handle.parent;
                if (controls === null) {
                    return;
                }
                controls.processHandle({ point: point, handle: handle, dragRatio: dragRatio });
                _this.eventListeners[RAYCASTER_EVENTS.DRAG].map(function (callback) {
                    callback(controls.object, handle.name);
                });
            });
            emitter.on(RAYCASTER_EVENTS.DRAG_STOP, function (_a) {
                var handle = _a.handle;
                if (handle === null) {
                    return;
                }
                var controls = handle.parent;
                if (controls === null) {
                    return;
                }
                controls.isBeingDraggedTranslation = false;
                controls.isBeingDraggedRotation = false;
                _this.eventListeners[RAYCASTER_EVENTS.DRAG_STOP].map(function (callback) {
                    callback(controls.object, handle.name);
                });
            });
        };
        _this.anchor = function (object, options) {
            var controls = _this.addControls(object, options);
            _this.objects[object.id] = object;
            return controls;
        };
        _this.detach = function (object, controls) {
            if (!_this.objects.hasOwnProperty(object.id)) {
                throw new Error("object should be attached first");
            }
            _this.remove(controls);
            _this.eventListeners[RAYCASTER_EVENTS.DRAG_STOP].map(function (callback) {
                callback(controls.object, null);
            });
            _this.dispose(controls);
            delete _this.objects[object.id];
            delete _this.controls[controls.id];
        };
        _this.addControls = function (object, options) {
            var controls = new Controls(object, _this.camera, options);
            _this.controls[controls.id] = controls;
            _this.add(controls);
            return controls;
        };
        _this.listen = function (event, callback) {
            _this.eventListeners[event].push(callback);
        };
        _this.removeListen = function (event, callback) {
            var index = _this.eventListeners[event].findIndex(callback);
            if (index !== -1) {
                _this.eventListeners[event].splice(index, 1);
            }
        };
        _this.dispose = function (object) {
            if (object instanceof Mesh) {
                object.geometry.dispose();
                if (Array.isArray(object.material)) {
                    object.material.map(function (material) { return material.dispose(); });
                }
                else {
                    object.material.dispose();
                }
            }
            while (object.children.length > 0) {
                object.children.map(function (child) {
                    _this.dispose(child);
                    object.remove(child);
                });
            }
        };
        _this.destroy = function () {
            var _a;
            unbindAll$1();
            var scene = _this.parent;
            if (scene !== null) {
                scene.remove(_this);
            }
            _this.dispose(_this);
            Object.values(_this.controls).map(function (control) {
                _this.dispose(control);
            });
            _this.eventListeners[RAYCASTER_EVENTS.DRAG_STOP].map(function (callback) {
                callback(null, null);
            });
            _this.rayCaster.destroy();
            _this.objects = {};
            _this.controls = {};
            _this.eventListeners = (_a = {},
                _a[RAYCASTER_EVENTS.DRAG_START] = [],
                _a[RAYCASTER_EVENTS.DRAG] = [],
                _a[RAYCASTER_EVENTS.DRAG_STOP] = [],
                _a);
        };
        _this.rayCaster = new Raycaster(_this.camera, _this.domElement, _this.controls);
        _this.listenToEvents();
        return _this;
    }
    return ControlsManager;
}(Object3D));

export { ANCHOR_MODE, ControlsManager, DEFAULT_HANDLE_GROUP_NAME, Pick, PickPlane, PickPlaneGroup, RAYCASTER_EVENTS, Rotation, RotationGroup, Translation, TranslationGroup };
//# sourceMappingURL=three-freeform-controls.es5.js.map
